import React from 'react';
import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { AlertDialogProps } from '@radix-ui/react-alert-dialog';

import { ToasterVariant, useToaster } from 'hooks/useToaster';
import {
  Modal,
  ModalTitle,
  ModalHeader,
  ModalFooter,
  ModalContent
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import {
  REMOVE_GUEST,
  RemoveGuestResult,
  RemoveGuestVar
} from 'graphql/mutations/guests/remove_guest';
import { EventType } from 'types/event.types';
import { CalendarProvider } from 'types/rsvp_control.types';
import { GuestStatus } from 'types/guest.types';

import { useGuestSideSheetContext } from '../guestSideSheetContext';

import { PlusOneStatusNotice } from './RsvpControlModal/RsvpControlModalContent/PlusOneStatusNotice';

interface RemoveGuestConfirmDialogProps extends AlertDialogProps {
  handleRemoveGuest: () => void;
}

export const RemoveGuestConfirmDialog: React.FC<RemoveGuestConfirmDialogProps> = ({
  open,
  onOpenChange,
  handleRemoveGuest
}) => {
  const { showToast } = useToaster();
  const {
    guestData: {
      guest,
      event: { calendarNotifications, calendarProvider, eventType, id: eventId }
    }
  } = useGuestSideSheetContext();
  const { email: gid } = guest;

  const [removeGuestMutation, { loading }] = useMutation<RemoveGuestResult, RemoveGuestVar>(
    REMOVE_GUEST,
    {
      onCompleted: handleRemoveGuest,
      onError: (error) => {
        showToast('Something went wrong', { variant: ToasterVariant.ERROR });
        Sentry.setContext('RSVP Control Remove Guest', { email: gid, event: eventId });
        Sentry.captureException(error);
      }
    }
  );
  const isMultiSession = eventType === EventType.MULTI_SESSIONS;
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="sm" className="tw-gap-5" showCloseButton={false}>
        <ModalHeader className="tw-mb-0">
          <ModalTitle>Are you sure?</ModalTitle>
        </ModalHeader>
        <div className="tw-flex tw-flex-col tw-gap-4 tw-text-dark-black">
          <p className="tw-m-0 tw-leading-5">
            This guest will be removed from the Epoch event
            {calendarNotifications ? ' and calendar event' : ''}
            {isMultiSession ? ' for upcoming sessions.' : '.'} They will be notified through
            {!calendarNotifications ? ' Email' : ''}
            {calendarProvider === CalendarProvider.GOOGLE ? ' Google Calendar' : ''}
            {calendarProvider === CalendarProvider.OUTLOOK ? ' Outlook Calendar' : ''} and Slack.
          </p>
          <PlusOneStatusNotice selectedStatus={GuestStatus.CANCELLED} />
          <p className="tw-m-0 tw-leading-5">You can always invite them back into the event.</p>
        </div>
        <ModalFooter className="tw-mt-0">
          <div className="tw-flex tw-items-end tw-justify-end tw-gap-3">
            <Button
              variant="secondary"
              size="sm"
              className="tw-rounded-[8px]"
              onClick={(): void => {
                if (onOpenChange) onOpenChange(false);
              }}
            >
              Stay on page
            </Button>
            <Button
              size="sm"
              className="tw-rounded-[8px]"
              variant="destructive"
              onClick={() => removeGuestMutation({ variables: { gid, eventId } })}
              loading={loading}
              disabled={loading}
            >
              Remove Guest
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
